import React, { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useLoginStore } from "@/Modules/Auth/Login/Store";
import { useRedirect } from "@/hooks";
import useApi, { ApiRequestError } from "@/hooks/useApi";
import { useCompanyStore, useLoggedUserStore } from "@/Store";
import ConnectionErrorScreen from "../ConnectionErrorScreen";
import PageLoader from "../PageLoader";
import {
  endImpersonationLocally,
  readImpersonationContext,
} from "@/Utils/authSession";

const RequiredAuth = () => {
  const sessionId = useLoginStore((state) => state.sessionId);
  const { clearSession } = useLoginStore();
  const redirect = useRedirect();
  const { getRequest } = useApi();
  const loggedUser = useLoggedUserStore((state) => state.loggedUser);
  const setLoggedUser = useLoggedUserStore((state) => state.setLoggedUser);
  const clearCompanyContext = useCompanyStore(
    (state) => state.clearCompanyContext
  );
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    const loadUser = async () => {
      setAuthUnavailable(false);
      try {
        const response = await getRequest("/api/user");
        const user = await response.json();
        if (active) setLoggedUser(user);
      } catch (requestError: unknown) {
        let effectiveError = requestError;
        if (
          requestError instanceof ApiRequestError &&
          requestError.status === 403
        ) {
          // Recupera apenas uma empresa persistida que não pertence à sessão.
          clearCompanyContext();
          try {
            const response = await getRequest("/api/user");
            const user = await response.json();
            if (active) setLoggedUser(user);
            return;
          } catch (retryError: unknown) {
            effectiveError = retryError;
          }
        }
        if (!active) return;
        if (
          effectiveError instanceof ApiRequestError &&
          effectiveError.status === 401
        ) {
          if (readImpersonationContext()) {
            window.location.assign(endImpersonationLocally());
            return;
          }
          setLoggedUser(null);
          clearSession();
          redirect("/login");
          return;
        }
        // Rede/5xx não invalidam uma sessão válida nem apagam a empresa.
        setAuthUnavailable(true);
      }
    };
    loadUser();
    return () => {
      active = false;
    };
  }, [sessionId, retryKey]);

  if (!sessionId) return <Navigate to="/login" replace />;

  // Aguarda a reidratação do usuário para evitar 404 em rotas protegidas no reload.
  if (sessionId && !loggedUser) {
    if (authUnavailable) {
      return (
        <ConnectionErrorScreen
          fullscreen
          description="Não conseguimos validar sua sessão agora. Ela continua salva: confira sua internet e tente de novo."
          onRetry={() => setRetryKey((value) => value + 1)}
        />
      );
    }
    return <PageLoader />;
  }

  return <Outlet />;
};

export default RequiredAuth;

